<template>
  <Container>
    <template #header>
      <Header :menu="$page.props.main_menu" v-if="$page.props.auth.user" />
    </template>
    <template #default>
      <div class="max-w-6xl mx-auto">
        <Breadcrumbs
          :breadcrumbs="[
            { name: 'Dashboard', href: route('dashboard') },
            { name: 'Berita', href: route('berita.index') },
            { name: 'Edit Berita' },
          ]"
        />

        <form class="space-y-6" @submit.prevent="submit">
          <!-- Title card -->
          <div class="bg-white border border-gray-200 shadow-lg rounded-xl">
            <div
              class="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-t-xl sm:px-6 sm:py-4"
            >
              <h2
                class="flex items-center text-lg font-bold text-white sm:text-xl"
              >
                <i class="mr-2 fa-solid fa-pen-to-square sm:mr-3"></i>
                Edit Berita
              </h2>
            </div>
            <div class="p-4 sm:p-6">
              <InputField
                label="Judul Berita"
                icon="pen"
                v-model="form.title"
                placeholder="Masukkan judul berita..."
              />
              <InputError :message="form.errors.title" class="mt-2" />
            </div>
          </div>

          <!-- Editor + sidebar -->
          <div class="grid grid-cols-1 gap-6 lg:grid-cols-12">
            <!-- Editor -->
            <div class="lg:col-span-8">
              <div
                class="p-4 bg-white border border-gray-200 shadow-lg sm:p-6 rounded-xl"
              >
                <InputLabel value="Konten Berita" />
                <div class="mt-2 quill-editor-wrapper">
                  <QuillEditor
                    ref="quillRef"
                    v-model="form.content"
                    :error="form.errors.content"
                    class="custom-quill-editor"
                  />
                </div>
                <InputError :message="form.errors.content" class="mt-2" />
              </div>
            </div>

            <!-- Sidebar -->
            <div class="lg:col-span-4">
              <div class="space-y-6 lg:sticky lg:top-24">
                <!-- Publikasi -->
                <div
                  class="bg-white border border-gray-200 shadow-sm rounded-xl"
                >
                  <div
                    class="px-4 py-3 border-b border-gray-200 rounded-t-xl bg-gray-50"
                  >
                    <h3
                      class="flex items-center text-sm font-semibold text-gray-800"
                    >
                      <i
                        class="mr-2 text-indigo-500 fa-solid fa-paper-plane"
                      ></i>
                      Publikasi
                    </h3>
                  </div>
                  <div class="p-4 space-y-4 sm:p-5">
                    <div>
                      <InputLabel for="publish_date" value="Tanggal Publish" />
                      <input
                        v-model="form.publish_date"
                        type="date"
                        id="publish_date"
                        name="publish_date"
                        class="block w-full px-3 py-2.5 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors duration-200"
                      />
                      <InputError
                        :message="form.errors.publish_date"
                        class="mt-2"
                      />
                    </div>

                    <div>
                      <InputLabel for="published" value="Status Publish" />
                      <select
                        v-model="form.published"
                        id="published"
                        class="block w-full px-3 py-2.5 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg shadow-sm appearance-none focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors duration-200"
                      >
                        <option value="0">Draft</option>
                        <option value="1">Publish</option>
                      </select>
                      <InputError
                        :message="form.errors.published"
                        class="mt-2"
                      />
                    </div>

                    <PrimaryBtn
                      :disabled="form.processing"
                      class="w-full px-4 py-2.5 text-sm font-semibold text-white bg-indigo-600 border border-transparent rounded-lg shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                    >
                      <i class="mr-2 fa-solid fa-floppy-disk"></i>
                      {{ form.processing ? "Menyimpan..." : "Perbarui Berita" }}
                    </PrimaryBtn>
                  </div>
                </div>

                <!-- Kategori / Tags -->
                <div
                  class="bg-white border border-gray-200 shadow-sm rounded-xl"
                >
                  <div
                    class="px-4 py-3 border-b border-gray-200 rounded-t-xl bg-gray-50"
                  >
                    <h3
                      class="flex items-center text-sm font-semibold text-gray-800"
                    >
                      <i class="mr-2 text-indigo-500 fa-solid fa-tags"></i>
                      Kategori / Tags
                    </h3>
                  </div>
                  <div class="p-4 sm:p-5">
                    <Multiselect
                      v-model="form.tags"
                      :options="tagOptions"
                      :multiple="true"
                      :taggable="true"
                      track-by="name"
                      label="name"
                      placeholder="Pilih atau tambah tag"
                      tag-placeholder="Tambah tag"
                      @tag="addTag"
                      class="w-full"
                    />
                    <InputError :message="form.errors.tags" class="mt-2" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </template>
  </Container>
</template>

<script setup>
import { ref } from "vue";
import { useForm } from "@inertiajs/vue3";
import Container from "@/Layouts/Container.vue";
import Header from "@/Components/Header.vue";
import Breadcrumbs from "@/Components/Breadcrumbs.vue";
import InputField from "@/Components/InputField.vue";
import InputLabel from "@/Components/InputLabel.vue";
import InputError from "@/Components/InputError.vue";
import PrimaryBtn from "@/Components/PrimaryBtn.vue";
import QuillEditor from "@/Components/QuillEditor.vue";
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.min.css";
import Swal from "sweetalert2";

const props = defineProps({
  berita: {
    type: Array,
    default: () => [],
  },
  allTags: {
    type: Array,
    default: () => [],
  },
  form: {
    type: Object,
    required: true,
  },
});

const quillRef = ref(null);
const tagOptions = ref([...props.allTags]);

const form = useForm({
  title: props.form.title || "",
  content: props.form.content || "",
  publish_date: props.form.publish_date || "",
  published: props.form.published ?? "0",
  tags: (props.form.tags || []).map((name) => ({ name })),
});

function addTag(newTag) {
  const exists =
    form.tags.some((t) => t.name === newTag) ||
    tagOptions.value.some((t) => t.name === newTag);
  if (!exists) {
    const tag = { name: newTag };
    tagOptions.value.push(tag);
    form.tags.push(tag);
  }
}

const submit = () => {
  Swal.fire({
    title: "Menyimpan...",
    text: "Sedang memperbarui berita",
    allowOutsideClick: false,
    didOpen: () => Swal.showLoading(),
  });

  form
    .transform((data) => ({
      ...data,
      tags: data.tags.map((t) => t.name ?? t),
    }))
    .put(route("berita.update", props.form.idnews), {
      onSuccess: () => {
        Swal.fire({
          title: "Berhasil!",
          text: "Berita berhasil diperbarui",
          icon: "success",
          confirmButtonText: "OK",
        });
      },
      onError: () => {
        Swal.fire({
          title: "Gagal!",
          text: "Periksa kembali isian Anda.",
          icon: "error",
          confirmButtonText: "OK",
        });
      },
    });
};
</script>

<style scoped>
.quill-editor-wrapper {
  width: 100%;
}
</style>
